/**
 * /frontend/src/features/analytics/pages/CustomerAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useCustomerAnalytics from '../hooks/useCustomerAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import GranularitySelector from '../components/shared/GranularitySelector';
import CustomerBehaviorKPIs from '../components/customers/CustomerBehaviorKPIs';
import CustomerSegmentPieChart from '../components/customers/CustomerSegmentPieChart';
import NewVsReturningTrendChart from '../components/customers/NewVsReturningTrendChart';
import VisitGapHistogram from '../components/customers/VisitGapHistogram';
import VisitFrequencyBuckets from '../components/customers/VisitFrequencyBuckets';
import {
  toCustomerGapHistogramSeries,
  toCustomerSegmentsPieSeries,
  toCustomerVisitTrendSeries,
  toVisitFrequencyBucketSeries
} from '../utils/chartDataTransformers';
import './CustomerAnalyticsPage.css';

const GRANULARITIES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'];

const getAtRiskCount = (segments) => {
  const row = (segments?.segments || []).find((s) => String(s?.segment) === 'AT_RISK');
  return Number(row?.count || 0);
};

/**
 * Customer analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const CustomerAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const [granularity, setGranularity] = useState('DAILY');
  const hook = useCustomerAnalytics({ request, storeToken, storeUuid, startDate, endDate, granularity, refreshSignal });
  const behavior = hook.data?.behavior || null;
  const segments = hook.data?.segments || null;
  const visitTrend = hook.data?.visitTrend || null;
  const syncing = hook.isLoading || hook.isRefreshing;

  const churnRiskCount = useMemo(() => getAtRiskCount(segments), [segments]);
  const segmentPie = useMemo(() => toCustomerSegmentsPieSeries(segments), [segments]);
  const trendSeries = useMemo(() => toCustomerVisitTrendSeries(visitTrend), [visitTrend]);
  const gapHistogram = useMemo(() => toCustomerGapHistogramSeries(behavior), [behavior]);
  const frequencyBuckets = useMemo(() => toVisitFrequencyBucketSeries(behavior), [behavior]);
  const isEmpty = useMemo(() => !behavior && !segments && !visitTrend, [behavior, segments, visitTrend]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No customer analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <div className="analytics-page-grid-2">
        <CustomerBehaviorKPIs behavior={behavior || {}} churnRiskCount={churnRiskCount} />
        <GranularitySelector value={granularity} options={GRANULARITIES} onChange={setGranularity} />
      </div>
      <div className="analytics-page-grid-2">
        <CustomerSegmentPieChart data={segmentPie} />
        <VisitFrequencyBuckets data={frequencyBuckets} />
      </div>
      <NewVsReturningTrendChart data={trendSeries} />
      <VisitGapHistogram data={gapHistogram} />
    </div>
  );
};

export default CustomerAnalyticsPage;
